import _ from 'underscore';
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getMultiCouponDescription } from './utils';
import { getMultiCouponDialogContentStyles } from '../styles/multi-coupon-dialog-content-styles';
import { DefaultCurrencyCode } from '../helpers';
import { MultiCouponDetailsProps } from '../coupon-props';

export const MultiCouponDialogContent = (props) => {
  const {
    oldCoupon,
    newCoupon,
    onSelectedCouponChange,
    getCouponCurrencyCode,
  } = props;

  const classes = useDefaultStyles(getMultiCouponDialogContentStyles);
  const i18n = useContext(I18nContext);

  const couponChoiceTypes = {
    newCoupon: {
      text: i18n.getString(_TL_('New coupon:')),
      key: 0,
    },
    oldCoupon: {
      text: i18n.getString(_TL_('Old coupon:')),
      key: 1,
    },
  };

  const getOptionText = useCallback((typeText, coupon) => (
    <div className={classNames(classes.optionText, 'coupon-choice-text')}>
      <span className={classNames(classes.optionTypeText, 'coupon-choice-type')}>
        {typeText}
      </span>
      <span className="coupon-choice-details">
        {getMultiCouponDescription(
          i18n,
          coupon.couponDetails,
          getCouponCurrencyCode(coupon.couponDetails.CurrencyId) || DefaultCurrencyCode
        )}
      </span>
    </div>
  ), [classes.optionText, classes.optionTypeText, i18n, getCouponCurrencyCode]);

  const choiceOptions = [
    {
      key: couponChoiceTypes.newCoupon.key,
      onRenderField: (choiceProps, render) => (
        <span className={classNames(classes.optionRoot, 'coupon-choice')}>
          {render(choiceProps)}
          {getOptionText(couponChoiceTypes.newCoupon.text, newCoupon)}
        </span>
      ),
    },
    {
      key: couponChoiceTypes.oldCoupon.key,
      onRenderField: (choiceProps, render) => (
        <span className={classNames(classes.optionRoot, 'coupon-choice')}>
          {render(choiceProps)}
          {getOptionText(couponChoiceTypes.oldCoupon.text, oldCoupon)}
        </span>
      ),
    },
  ];

  const onSelectionChange = useCallback((data, option) => {
    const selectedCoupon = option.key === couponChoiceTypes.newCoupon.key ? newCoupon : oldCoupon;
    onSelectedCouponChange(selectedCoupon);
  }, [couponChoiceTypes.newCoupon.key, newCoupon, oldCoupon, onSelectedCouponChange]);

  if (!newCoupon || !oldCoupon) {
    return null;
  }

  return (
    <div className={classNames(classes.couponContainer, classes.confirmDialogOverride, 'multi-coupon-container')}>
      <div className={classNames(classes.couponWarningDetails, 'multi-coupon-warning-details')}>
        {i18n.getString(_TL_('You can only claim one new customer coupon and you just tried to claim a second coupon. Please choose which coupon you\'d like to use:'))}
      </div>

      <ChoiceGroup
        defaultSelectedKey={couponChoiceTypes.newCoupon.key}
        options={choiceOptions}
        onChange={onSelectionChange}
        className={classNames(classes.choiceGroup, classes.confirmDialogOverride)}
      />
    </div>
  );
};

MultiCouponDialogContent.propTypes = {
  oldCoupon: MultiCouponDetailsProps,
  newCoupon: MultiCouponDetailsProps,
  onSelectedCouponChange: PropTypes.func.isRequired,
  getCouponCurrencyCode: PropTypes.func,
};

MultiCouponDialogContent.defaultProps = {
  oldCoupon: null,
  newCoupon: null,
  getCouponCurrencyCode: _.noop,
};
